import React, { useState, useEffect } from "react";
import { formatCpf } from "@brazilian-utils/brazilian-utils";
import styled from "styled-components";
import Navbar from "../../components/layout/Navbar/Navbar";
import Sidebar from "../../components/layout/Sidebar/Sidebar";
import UsersActiveHeader from "../../components/features/users/UsersAtivosHeader";
import { getCargoIdSidebar } from "../../constants/sidebarConfig";
import apiService from "../../services/api";
import { applyUserFilters } from "../../utils/userFilters";
import { usePopup } from "../../components/common/Popup";
import "../../components/features/users/user-table.css";
import trashIcon from "../../assets/trash.png";
import visibilityIcon from "../../assets/visibility.png";
import ProfileUserInfo from "../../components/Profile/ProfileUserInfo";

const NAVBAR_HEIGHT = 65;
const SIDEBAR_WIDTH = 300;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f9f9f9;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  margin-top: 60px;
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${SIDEBAR_WIDTH}px;
  overflow-y: auto;
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
`;


const UsersAtivos = () => {
  const popup = usePopup();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [sidebarItems, setSidebarItems] = useState([]);
  const [filters, setFilters] = useState({
    cargoId: null,
    sortBy: 'nome',
    order: 'asc',
    searchTerm: ''
  });
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileModalUser, setProfileModalUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem('token');

  const openUserProfile = async (rowUser) => {
    setProfileModalOpen(true);
    setProfileModalUser(null);
    setProfileLoading(true);
    try {
      const full = await apiService.getUsuarioById(rowUser.id);
      setProfileModalUser(full);
    } catch (err) {
      console.error(err);
      setProfileModalOpen(false);
      await popup.alert({
        icon: 'warning',
        title: 'Erro',
        message: 'Não foi possível carregar o perfil do usuário.',
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const closeUserProfile = () => {
    setProfileModalOpen(false);
    setProfileModalUser(null);
  };

  const deleteUser = async (id) => {
    const confirmed = await popup.confirm({
      icon: 'delete',
      message: 'Deseja excluir este usuário?',
    });
    if (!confirmed) return;

    try {
      const response = await fetch(`${baseUrl}/usuario/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Erro ao excluir usuário');

      setUsers((prev) => prev.filter((user) => user.id !== id));
    } catch (err) {
      console.error(err);
      await popup.alert({
        icon: 'warning',
        title: 'Erro ao excluir',
        message: 'Não foi possível excluir o usuário.',
      });
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${baseUrl}/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const activeUsers = data.filter(user => user.status === 'ATIVO');

      setUsers(activeUsers);
      const filtered = applyUserFilters(activeUsers, filters);
      setFilteredUsers(filtered);
    } catch (err) {
      console.error('Erro na requisição:', err);
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await apiService.getCurrentUser();
        setUser(userData);
        const filteredItems = getCargoIdSidebar(userData.cargoId);
        setSidebarItems(filteredItems);
      } catch (err) {
        console.error('Erro ao buscar usuário:', err);
      }
    };

    fetchUser();
    fetchUsers();
  }, []);

  const handleFilterChange = (cargoId) => {
    const newFilters = { ...filters, cargoId: cargoId === 'all' ? null : cargoId };
    setFilters(newFilters);
    const filtered = applyUserFilters(users, newFilters);
    setFilteredUsers(filtered);
  };

  const handleSortChange = ({ sortBy, order }) => {
    const newFilters = { ...filters, sortBy, order };
    setFilters(newFilters);
    const filtered = applyUserFilters(users, newFilters);
    setFilteredUsers(filtered);
  };

  const handleSearchChange = (term) => {
    const newFilters = { ...filters, searchTerm: term };
    setFilters(newFilters);
    const filtered = applyUserFilters(users, newFilters);
    setFilteredUsers(filtered);
  };

  useEffect(() => {
    if (users.length > 0) {
      const filtered = applyUserFilters(users, filters);
      setFilteredUsers(filtered);
    }
  }, [filters, users]);

  return (
    <PageWrapper>
      <Navbar 
        onLogout={apiService.logout}
        showHome={true}
        showNotifications={true}
        showProfile={true}
        showLogout={true}
        user={user}
      />
      <ContentWrapper>
        <Sidebar items={sidebarItems} />
        <MainContent>
          <UsersActiveHeader 
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
            searchValue={filters.searchTerm}
            onSearchChange={handleSearchChange}
          />
          <div className="users-container">
            <div className="section">
              <div className="table-header">
                <h4 className="active-title">Usuários Ativos</h4>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>NOME</th>
                      <th>EMAIL</th>
                      <th>CPF</th>
                      <th>CARGO</th>
                      <th className="actions-header">AÇÕES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.nome}</td>
                        <td>{user.email}</td>
                        <td>{user.cpf ? formatCpf(user.cpf) : '-'}</td>
                        <td>{user.cargo || '-'}</td>
                        <td className="actions">
                          <div className="actions-single">
                            <button
                              type="button"
                              className="btn-view-icon"
                              onClick={() => openUserProfile(user)}
                              title="Ver perfil"
                            >
                              <img src={visibilityIcon} alt="Ver" style={{ width: 22, height: 22 }} />
                            </button>
                            <button
                              type="button"
                              className="btn-delete-icon"
                              onClick={() => deleteUser(user.id)}
                              title="Deletar usuário"
                            >
                              <img src={trashIcon} alt="Deletar" style={{ width: 22, height: 22 }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan="5" className="no-data">
                          {users.length === 0 ? 'Nenhum usuário ativo encontrado' : 'Nenhum usuário encontrado com os filtros aplicados'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {profileModalOpen && (
            <div
              className="modal-overlay"
              role="presentation"
              onClick={closeUserProfile}
            >
              <div
                className="modal-content user-profile-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="user-profile-modal-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3 id="user-profile-modal-title">Perfil do usuário</h3>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={closeUserProfile}
                    aria-label="Fechar"
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body user-profile-modal-body">
                  {profileLoading && <p>Carregando...</p>}
                  {!profileLoading && profileModalUser && (
                    <ProfileUserInfo
                      key={profileModalUser.id}
                      userData={profileModalUser}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </MainContent>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default UsersAtivos;
